import { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { Plus, Edit, Trash2, LogOut, X, Save, FileText } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";
import { ImageUploader, resolveImageUrl } from "@/components/ImageUploader";

const emptyProject = {
  title: "",
  slug: "",
  location: "",
  status: "devam_ediyor",
  short_description: "",
  description: "",
  hero_image: "",
  gallery: [],
  features: [],
  social_areas: [],
  location_advantages: [],
  order: 0,
};

const slugify = (s) =>
  s
    .toLowerCase()
    .replace(/ş/g, "s").replace(/ç/g, "c").replace(/ğ/g, "g")
    .replace(/ı/g, "i").replace(/ö/g, "o").replace(/ü/g, "u")
    .replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

export default function AdminDashboard() {
  const { user, logout, loading } = useAuth();
  const [projects, setProjects] = useState([]);
  const [editing, setEditing] = useState(null); // null | {} | project
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/projects").then((r) => setProjects(r.data));

  useEffect(() => {
    if (user) load();
  }, [user]);

  if (loading) return <div className="min-h-screen flex items-center justify-center text-[var(--slate)]">Yükleniyor…</div>;
  if (!user) return <Navigate to="/admin/login" replace />;

  const startNew = () => setEditing({ ...emptyProject });
  const startEdit = (p) => setEditing({ ...p });

  const onDelete = async (p) => {
    if (!window.confirm(`"${p.title}" silinsin mi?`)) return;
    try {
      await api.delete(`/projects/${p.id}`);
      toast.success("Proje silindi");
      await load();
    } catch (e) {
      toast.error("Silme başarısız");
    }
  };

  const onSave = async (e) => {
    e.preventDefault();
    if (!editing.title || !editing.slug || !editing.hero_image) {
      toast.error("Başlık, slug ve hero görsel zorunludur.");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...editing,
        gallery: typeof editing.gallery === "string" ? editing.gallery.split("\n").map((s) => s.trim()).filter(Boolean) : editing.gallery,
        features: typeof editing.features === "string" ? editing.features.split("\n").map((s) => s.trim()).filter(Boolean) : editing.features,
        social_areas: typeof editing.social_areas === "string" ? editing.social_areas.split("\n").map((s) => s.trim()).filter(Boolean) : editing.social_areas,
        location_advantages: typeof editing.location_advantages === "string" ? editing.location_advantages.split("\n").map((s) => s.trim()).filter(Boolean) : editing.location_advantages,
        order: Number(editing.order) || 0,
      };
      if (editing.id) {
        await api.put(`/projects/${editing.id}`, payload);
        toast.success("Proje güncellendi");
      } else {
        await api.post("/projects", payload);
        toast.success("Proje oluşturuldu");
      }
      setEditing(null);
      await load();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Kayıt başarısız");
    } finally {
      setSaving(false);
    }
  };

  // Convert array fields to newline-joined strings when starting edit
  const toTextArea = (v) => (Array.isArray(v) ? v.join("\n") : v || "");

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-[var(--ivory)]">
      {/* Topbar */}
      <header className="border-b border-[var(--gold)]/25 bg-[var(--cream)] px-6 lg:px-12 py-5 flex items-center justify-between">
        <div className="flex items-center gap-6">
          <Logo variant="light" size="md" />
          <span className="hidden md:inline text-xs tracking-[0.22em] uppercase text-[var(--slate)]">Yönetim Paneli</span>
        </div>
        <div className="flex items-center gap-3">
          <Link to="/admin/icerik" className="btn-ghost-gold" data-testid="admin-content-link">
            <FileText size={16} /> Site İçeriği
          </Link>
          <Link to="/" className="text-xs tracking-[0.18em] uppercase text-[var(--navy)] hover:text-[var(--gold)]" data-testid="admin-back-to-site">Siteyi Gör</Link>
          <button onClick={logout} className="btn-ghost-gold" data-testid="admin-logout">
            <LogOut size={16} /> Çıkış
          </button>
        </div>
      </header>

      <main className="px-6 lg:px-12 py-10 max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-serif-luxe text-4xl text-[var(--navy)]">Projeler</h1>
            <p className="text-sm text-[var(--slate)] font-light mt-1">Projelerinizi yönetin, düzenleyin ve yenilerini ekleyin.</p>
          </div>
          <button onClick={startNew} className="btn-primary" data-testid="admin-new-project">
            <Plus size={16} /> Yeni Proje
          </button>
        </div>

        <div className="grid gap-4">
          {projects.map((p) => (
            <div key={p.id} className="flex flex-col md:flex-row gap-4 lux-card p-4" data-testid={`admin-project-row-${p.slug}`}>
              <img src={resolveImageUrl(p.hero_image)} alt={p.title} className="w-full md:w-48 h-32 object-cover" />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-3 flex-wrap">
                  <h3 className="font-serif-luxe text-2xl text-[var(--navy)]">{p.title}</h3>
                  <span className={`text-[0.65rem] tracking-[0.18em] uppercase px-2 py-1 border ${p.status === "tamamlandi" ? "bg-[var(--navy)] text-[var(--gold)] border-[var(--navy)]" : "bg-[var(--gold)] text-[var(--navy)] border-[var(--gold)]"}`}>
                    {p.status === "tamamlandi" ? "Tamamlandı" : "Devam"}
                  </span>
                </div>
                <p className="text-xs tracking-[0.18em] uppercase text-[var(--slate)] mt-1">{p.location}</p>
                <p className="text-sm text-[var(--slate)] font-light mt-2 line-clamp-2">{p.short_description}</p>
              </div>
              <div className="flex md:flex-col gap-2 items-start">
                <button onClick={() => startEdit(p)} className="btn-ghost-gold !py-2 !px-4" data-testid={`admin-edit-${p.slug}`}>
                  <Edit size={14} /> Düzenle
                </button>
                <button onClick={() => onDelete(p)} className="!py-2 !px-4 inline-flex items-center gap-2 text-xs tracking-[0.18em] uppercase border border-red-500/40 text-red-600 hover:bg-red-500 hover:text-[var(--ivory)] transition-colors" data-testid={`admin-delete-${p.slug}`}>
                  <Trash2 size={14} /> Sil
                </button>
              </div>
            </div>
          ))}
          {projects.length === 0 && (
            <p className="text-center text-[var(--slate)] py-12">Henüz proje yok.</p>
          )}
        </div>
      </main>

      {/* Drawer for editing */}
      {editing && (
        <div className="fixed inset-0 z-50 bg-[var(--navy)]/60 backdrop-blur-sm flex justify-end" data-testid="admin-editor">
          <form
            onSubmit={onSave}
            className="w-full max-w-2xl bg-[var(--ivory)] h-full overflow-y-auto border-l border-[var(--gold)]/30"
          >
            <div className="sticky top-0 bg-[var(--cream)] border-b border-[var(--gold)]/25 px-8 py-5 flex items-center justify-between z-10">
              <h2 className="font-serif-luxe text-2xl text-[var(--navy)]">
                {editing.id ? "Projeyi Düzenle" : "Yeni Proje"}
              </h2>
              <button type="button" onClick={() => setEditing(null)} className="text-[var(--navy)] hover:text-[var(--gold)]" data-testid="admin-editor-close">
                <X size={22} />
              </button>
            </div>

            <div className="p-8 space-y-6">
              <FormField label="Başlık *">
                <input
                  required
                  value={editing.title}
                  onChange={(e) => setEditing({ ...editing, title: e.target.value, slug: editing.slug || slugify(e.target.value) })}
                  data-testid="admin-field-title"
                  className="lux-input"
                />
              </FormField>
              <FormField label="Slug * (URL)">
                <input required value={editing.slug} onChange={(e) => setEditing({ ...editing, slug: slugify(e.target.value) })} className="lux-input" data-testid="admin-field-slug" />
              </FormField>
              <FormField label="Konum">
                <input value={editing.location} onChange={(e) => setEditing({ ...editing, location: e.target.value })} className="lux-input" data-testid="admin-field-location" />
              </FormField>
              <FormField label="Durum">
                <select value={editing.status} onChange={(e) => setEditing({ ...editing, status: e.target.value })} className="lux-input" data-testid="admin-field-status">
                  <option value="devam_ediyor">Devam Ediyor</option>
                  <option value="tamamlandi">Tamamlandı</option>
                </select>
              </FormField>
              <FormField label="Kısa Açıklama">
                <textarea rows="2" value={editing.short_description} onChange={(e) => setEditing({ ...editing, short_description: e.target.value })} className="lux-input" data-testid="admin-field-short" />
              </FormField>
              <FormField label="Uzun Açıklama">
                <textarea rows="6" value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} className="lux-input" data-testid="admin-field-desc" />
              </FormField>

              <div data-testid="admin-field-images-block">
                <ImageUploader
                  label="Proje Fotoğrafları (ana görsel + galeri)"
                  value={(() => {
                    // Combine hero + gallery, ensuring hero appears once and first
                    const gal = Array.isArray(editing.gallery)
                      ? editing.gallery
                      : (editing.gallery || "").split("\n").map((s) => s.trim()).filter(Boolean);
                    const all = editing.hero_image ? [editing.hero_image, ...gal.filter((g) => g !== editing.hero_image)] : gal;
                    return all;
                  })()}
                  onChange={(next) => {
                    // First item becomes hero if no hero selected; otherwise keep current hero if still present
                    let newHero = editing.hero_image;
                    if (!next.includes(newHero)) newHero = next[0] || "";
                    setEditing({ ...editing, hero_image: newHero, gallery: next.filter((u) => u !== newHero) });
                  }}
                  primaryUrl={editing.hero_image}
                  onSetPrimary={(url) => {
                    const gal = (Array.isArray(editing.gallery) ? editing.gallery : []).filter((u) => u !== url);
                    // put old hero back into gallery if it exists
                    if (editing.hero_image && editing.hero_image !== url && !gal.includes(editing.hero_image)) {
                      gal.unshift(editing.hero_image);
                    }
                    setEditing({ ...editing, hero_image: url, gallery: gal });
                  }}
                  testIdPrefix="admin-images"
                />
                <p className="text-xs text-[var(--slate)] mt-2 font-light">
                  Yıldız simgesine tıklayarak ana görseli (hero) seçin. Sırasıyla yüklediğiniz görseller galeride görünür.
                </p>
              </div>
              <FormField label="Özellikler (her satıra 1 madde)">
                <textarea rows="4" value={toTextArea(editing.features)} onChange={(e) => setEditing({ ...editing, features: e.target.value })} className="lux-input" data-testid="admin-field-features" />
              </FormField>
              <FormField label="Sosyal Alanlar (her satıra 1 madde)">
                <textarea rows="3" value={toTextArea(editing.social_areas)} onChange={(e) => setEditing({ ...editing, social_areas: e.target.value })} className="lux-input" data-testid="admin-field-social" />
              </FormField>
              <FormField label="Konum Avantajları (her satıra 1 madde)">
                <textarea rows="3" value={toTextArea(editing.location_advantages)} onChange={(e) => setEditing({ ...editing, location_advantages: e.target.value })} className="lux-input" data-testid="admin-field-location-adv" />
              </FormField>
              <FormField label="Sıralama">
                <input type="number" value={editing.order} onChange={(e) => setEditing({ ...editing, order: e.target.value })} className="lux-input" data-testid="admin-field-order" />
              </FormField>

              <div className="pt-4 flex gap-3">
                <button type="submit" disabled={saving} className="btn-primary" data-testid="admin-save">
                  <Save size={16} /> {saving ? "Kaydediliyor…" : "Kaydet"}
                </button>
                <button type="button" onClick={() => setEditing(null)} className="btn-ghost-gold">
                  İptal
                </button>
              </div>
            </div>
          </form>
        </div>
      )}

      <style>{`
        .lux-input {
          width: 100%;
          background: transparent;
          border: 1px solid rgba(10, 25, 47, 0.2);
          padding: 0.6rem 0.85rem;
          font-family: "Outfit", sans-serif;
          font-weight: 300;
          color: var(--ink);
          outline: none;
          transition: border-color 200ms ease;
        }
        .lux-input:focus { border-color: var(--gold); }
      `}</style>
    </div>
  );
}

const FormField = ({ label, children }) => (
  <div>
    <label className="block text-xs tracking-[0.18em] uppercase text-[var(--navy)] mb-2">{label}</label>
    {children}
  </div>
);
